/* ZUZU room guides — the "benefits" and "how to use" content for each of the
   five rooms.

   Split out of data.js because it's long-form member copy that changes on its
   own schedule (a new machine, a new preset, a new study), while data.js is
   specs and plumbing. data.js merges both onto each modality, so
   `room.benefits` / `room.guide` read like any other modality field.

   BENEFITS — one shape for every room:
     title · kicker · items[] · early? (a softer "still being studied" list)
     · foot (the "results vary" line every room carries).
   `items` is also the single source of truth for the room's `why` list, so
   the app tab, the /rooms long-read and the room's own page can never drift.

   GUIDE — an ordered list of blocks, because the rooms genuinely don't have
   the same shape: the plunge is a choice between two tubs, the sauna is a
   checklist and a timer, compression is a machine with buttons. Each block
   names its `kind` and RoomGuide (room.jsx) renders it:

     steps     numbered title + body cards
     duo       two side-by-side cards; tone:'ink' makes one dark
     checklist ticked list under a label
     feature   the one dark card on the page — a stat, a dial, or a line to
               remember. `action:'scan'` adds the real start-scan button.
     rows      01/02/03 rows; `mark` bolds the button name inside the text
     chips     pill list (best for · presets)
     note      tinted callout — rose for safety, sand for a tip, moss for a
               nudge. `mark` bolds a phrase inside `text`.

   Brand voice: lowercase throughout (see CLAUDE.md). The exceptions here are
   labels printed on the equipment itself — ON/OFF, LEVEL, TIME, START,
   SCENE, ZONEBOOST — which stay as they read on the machine so a member can
   find the button, plus acronyms (NIR, DEXA) and the ZUZU mark.
*/

// Every room carries the same honest footnote under its benefits.
const VARIES =
  "benefits vary between people and come from regular use, not one session.";

export const ZZ_BENEFITS = {
  plunge: {
    title: "why cold works",
    kicker: "what the cold does once you are out of the tub.",
    items: [
      "eases muscle soreness after a hard workout.",
      "helps your body calm down faster after intense exercise.",
      "lowers stress and helps you sleep better.",
      "helps you feel recovered and ready to go again the next day.",
      "cuts the number of sick days you take.",
      "leaves you feeling alert and lifted afterwards.",
    ],
    foot: VARIES,
  },

  sauna: {
    title: "why heat works",
    kicker: "some of the strongest evidence in recovery.",
    items: [
      "regular sauna users have markedly lower rates of heart disease and stroke.",
      "regular users are far less likely to develop high blood pressure.",
      "in an 8-week study, adding a sauna after training more than doubled fitness gains compared with training alone, and dropped blood pressure by 8 points.",
      "people who sauna 4 to 7 times a week are diagnosed with dementia at about a third the rate of once-a-week users.",
      "eases stress and helps you switch off.",
      "members report better sleep and less muscle soreness after regular use.",
    ],
    foot: VARIES,
  },

  red: {
    title: "why red light",
    kicker: "strongest for skin, with more still being studied.",
    items: [
      "reduces the look of fine lines and wrinkles.",
      "improves skin smoothness and texture.",
      "increases collagen for firmer-looking skin.",
      "members report feeling better about how their skin looks after a course of sessions.",
    ],
    early: {
      label: "early research points to",
      items: [
        "easing muscle soreness.",
        "supporting hair growth.",
        "easing joint pain and stiffness.",
      ],
    },
    foot: VARIES,
  },

  comp: {
    title: "why it works",
    kicker: "what members notice from regular compression sessions.",
    items: [
      "eases muscle soreness, most noticeably the day after a hard session.",
      "legs feel lighter and less heavy straight away.",
      "reduces swelling and stiffness after exercise.",
      "increases blood flow to tired muscles.",
      "helps you feel less fatigued and readier to go again.",
      "eases the ache of being on your feet all day, not just after a workout.",
    ],
    foot: VARIES,
  },

  inbody: {
    title: "why scan",
    kicker: "what the numbers give you that a scale cannot.",
    items: [
      "gives you a trend you can trust, not just a single number.",
      "closely matches hospital-grade body scans.",
      "flags visceral fat, an early warning sign a scale alone will not show you.",
      "tracks muscle mass, which matters more as you get older.",
      "no radiation, unlike a DEXA scan.",
      "shows if one side of your body is out of balance with the other, so you know what to work on.",
    ],
    foot: VARIES,
  },
};

export const ZZ_GUIDES = {
  plunge: {
    title: "cold plunge",
    kicker: "choose your cold.",
    blocks: [
      {
        kind: "duo",
        items: [
          {
            k: "left",
            big: "12°",
            body: "gentler. start here.",
            note: "2–5 min",
          },
          {
            k: "right",
            big: "7°",
            body: "colder. experienced.",
            note: "1–3 min",
            tone: "ink",
          },
        ],
      },
      {
        kind: "checklist",
        label: "before you get in",
        items: [
          "rinse off in the shower",
          "remove jewellery and valuables",
          "take a few slow breaths",
          "careful, surfaces may be slippery",
        ],
      },
      {
        kind: "feature",
        label: "while you're in",
        headline: "slow inhale → long exhale → relax.",
        body: "your first instinct is to breathe fast. don't. and never hold your breath.",
      },
      {
        kind: "note",
        tone: "rose",
        icon: "bell",
        title: "emergency bell on the wall",
        text: "dizzy, faint, numb or unwell? get out and ring it.",
      },
    ],
    foot: "shorter is completely fine. take it slow, stay calm, listen to your body.",
  },

  sauna: {
    title: "sauna",
    kicker: "your heat reset",
    blocks: [
      {
        kind: "checklist",
        label: "before you enter",
        items: [
          "rinse off first",
          "bring a towel",
          "remove jewellery",
          "electronics stay outside",
          "drink water",
        ],
      },
      {
        kind: "feature",
        dial: { n: "15", unit: "minutes" },
        headline: "flip the timer inside to start.",
        body: "reset it for another 15 after a cool-down.",
      },
      {
        kind: "duo",
        label: "how long",
        items: [
          { k: "starting out", big: "10–15 min" },
          { k: "experienced", big: "30–45 min" },
        ],
      },
      {
        kind: "note",
        tone: "rose",
        mark: "leave immediately",
        text: "leave immediately if you feel dizzy, faint, nauseous or unwell. hydrate before and after.",
      },
    ],
    foot: "enjoy the heat. switch off from the outside world.",
  },

  red: {
    title: "red light therapy",
    kicker: "targeted light. personalised recovery.",
    blocks: [
      {
        kind: "duo",
        items: [
          { k: "face panel", body: "facial skin and targeted treatments." },
          { k: "body panel", body: "larger areas and full-body recovery." },
        ],
      },
      {
        kind: "feature",
        label: "before you start",
        headline: "glasses on. eyes closed.",
        body: "clean dry skin · jewellery off · towel on the bed.",
      },
      {
        kind: "rows",
        label: "at the panel",
        items: [
          { text: "power on at the back of the unit" },
          { text: "press SCENE, pick your goal", mark: "SCENE" },
          { text: "the preset sets time and settings" },
          { text: "press START / PAUSE", mark: "START / PAUSE" },
          { text: "relax, it stops on its own" },
        ],
      },
      {
        kind: "chips",
        label: "presets",
        items: [
          "skin",
          "muscle recovery",
          "sleep",
          "joint / pain",
          "fat loss",
          "universal",
        ],
      },
      {
        kind: "note",
        tone: "sand",
        text: "a beep halfway through is your cue to change position if you're treating another area.",
      },
    ],
    foot: "you don't need the technical settings. choose your goal, glasses on, press start.",
  },

  comp: {
    title: "compression boots",
    kicker: "your compression session",
    blocks: [
      {
        kind: "steps",
        items: [
          ["get comfortable", "sit or recline with your legs supported."],
          ["put on the boots", "slide your legs in and zip up comfortably."],
          ["power on", "press ON/OFF on the side of the battery pack."],
          ["set your intensity", "firm and rhythmic, never painful."],
          ["relax & recover", "sit back and let it cycle."],
        ],
      },
      { kind: "feature", label: "typical session", big: "20–60 min" },
      {
        kind: "chips",
        label: "best for",
        items: [
          "post-workout",
          "heavy legs",
          "stiffness",
          "circulation",
          "warm-up",
        ],
      },
      {
        kind: "rows",
        label: "the battery pack",
        items: [
          { text: "ON/OFF, on the side of the pack", mark: "ON/OFF" },
          { text: "LEVEL, 1 gentle, 7 strong", mark: "LEVEL" },
          { text: "TIME, set your duration", mark: "TIME" },
          { text: "START, on the top of the pack", mark: "START" },
          { text: "sit back and let it work" },
        ],
      },
      {
        kind: "note",
        tone: "rose",
        mark: "ZONEBOOST",
        text: "ZONEBOOST, extra compression in one zone, if you want it.",
      },
    ],
    foot: "stop if you feel pain, numbness, tingling or real discomfort. if you've been advised to avoid compression therapy, speak to a healthcare professional first.",
  },

  inbody: {
    title: "body composition scan",
    kicker: "your scan in five steps",
    blocks: [
      // The one guide block that does something: the same scan the buttons
      // above the tabs start, offered where a member is actually reading the
      // steps.
      {
        kind: "feature",
        headline: "ready when you are.",
        action: "scan",
        actionLabel: "start scan",
      },
      {
        kind: "steps",
        items: [
          ["log in", "open the ZUZU app."],
          [
            "start your scan",
            "tap scan on the home page, or equipment > scan.",
          ],
          ["feet on the plates", "bare feet, no socks."],
          [
            "lift the handle",
            "fingers over the top, thumbs on the silver pad nearest you. then stay still.",
          ],
          ["check your results", "available in the app right after."],
        ],
      },
      {
        kind: "note",
        tone: "moss",
        label: "for better comparisons",
        title: "consistency > perfection.",
        text: "scan under similar conditions. track the trend, not one reading.",
      },
    ],
  },
};
